import { useEffect, useRef, useState } from 'react';

/**
 * Shared SVG-chart scaling helpers for the dashboard chart kit (09-dashboard).
 * The charts are hand-rolled SVG (no charting library — see the slice log) so
 * they match the prototype pixel-for-pixel and add no dependency.
 */

/**
 * Measures a container's content width via `ResizeObserver` so an SVG chart can
 * render responsively without a fixed viewBox. Returns a ref to attach to the
 * measured element and the current integer width. Starts at `initial` for the
 * first paint (and any SSR pass), then settles on the real width.
 */
export function useMeasuredWidth(initial = 800): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(initial);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0) setWidth(Math.floor(rect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/**
 * Computes "nice" axis ticks for a max value: a rounded step (1/2/5 × 10ⁿ) and
 * the ceiling `niceMax` so the top gridline sits on a round number. Returns the
 * inclusive tick list and the scaled maximum. Guards non-positive input.
 */
export function niceTicks(max: number, count = 5): { ticks: number[]; niceMax: number } {
  if (max <= 0) return { ticks: [0], niceMax: 1 };
  const raw = max / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / magnitude;
  const step =
    (normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10) * magnitude;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) ticks.push(v);
  return { ticks, niceMax };
}
